"use client";

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { loadGalleryImages } from "../utils/galleryLoader";

const EventGallery = ({ galleryFolder, title = "Event Gallery" }) => {
	const [images, setImages] = useState([]);
	const [selectedImage, setSelectedImage] = useState(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const loadImages = async () => {
			try {
				setLoading(true);
				const galleryImages = await loadGalleryImages(galleryFolder);
				setImages(galleryImages);
			} catch (error) {
				console.error("Error loading gallery images:", error);
				setImages([]);
			} finally {
				setLoading(false);
			}
		};

		if (galleryFolder) {
			loadImages();
		} else {
			setLoading(false);
			setImages([]);
		}
	}, [galleryFolder]);

	const openLightbox = (image) => setSelectedImage(image);
	const closeLightbox = () => setSelectedImage(null);

	const nextImage = () => {
		const idx = images.findIndex((img) => img.id === selectedImage.id);
		setSelectedImage(images[(idx + 1) % images.length]);
	};

	const prevImage = () => {
		const idx = images.findIndex((img) => img.id === selectedImage.id);
		setSelectedImage(images[idx === 0 ? images.length - 1 : idx - 1]);
	};

	// Keyboard navigation
	useEffect(() => {
		if (!selectedImage) return;
		const handleKey = (e) => {
			if (e.key === "ArrowRight") nextImage();
			if (e.key === "ArrowLeft") prevImage();
			if (e.key === "Escape") closeLightbox();
		};
		window.addEventListener("keydown", handleKey);
		return () => window.removeEventListener("keydown", handleKey);
	}, [selectedImage, images]);

	if (loading) {
		return (
			<div className="py-12 sm:py-16">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<h2 className="text-2xl sm:text-3xl font-bold text-[#1666ba] mb-8 text-center">{title}</h2>
					<div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
						{Array.from({ length: 6 }).map((_, i) => (
							<div key={i} className="bg-gray-200 animate-pulse rounded-lg h-40 sm:h-64"></div>
						))}
					</div>
				</div>
			</div>
		);
	}

	if (!images.length) {
		return (
			<div className="py-12 sm:py-16 bg-gray-50">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<h2 className="text-2xl sm:text-3xl font-bold text-[#1666ba] mb-8 text-center">{title}</h2>
					<div className="text-center text-gray-600 max-w-2xl mx-auto">
						<svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5"
								d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
						</svg>
						<h3 className="text-xl font-semibold text-gray-700 mb-2">Gallery Coming Soon</h3>
						<p className="text-gray-600">
							Photos from this event will be added to the gallery soon. Check back later to see highlights from this amazing event!
						</p>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="py-12 sm:py-16 bg-gray-50">
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
				<motion.h2
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6 }}
					className="text-2xl sm:text-3xl font-bold text-[#1666ba] mb-8 text-center">
					{title}
				</motion.h2>

				<div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
					{images.map((image, index) => (
						<motion.div
							key={image.id}
							initial={{ opacity: 0, scale: 0.9 }}
							whileInView={{ opacity: 1, scale: 1 }}
							transition={{ duration: 0.4, delay: Math.min(index * 0.05, 0.5) }}
							className="relative group cursor-pointer"
							onClick={() => openLightbox(image)}>
							<div className="overflow-hidden rounded-lg shadow-md">
								<img
									src={image.src}
									alt={image.alt}
									loading="lazy"
									className="w-full h-36 sm:h-52 lg:h-64 object-cover transition-transform duration-300 group-hover:scale-105"
								/>
								<div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 rounded-lg flex items-center justify-center">
									<div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
										<svg className="w-10 h-10 text-white drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
												d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
										</svg>
									</div>
								</div>
							</div>
						</motion.div>
					))}
				</div>

				{/* Lightbox */}
				{selectedImage && (
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center"
						onClick={closeLightbox}>
						<div
							className="relative w-full h-full flex items-center justify-center px-12 sm:px-16"
							onClick={(e) => e.stopPropagation()}>
							<img
								src={selectedImage.src}
								alt={selectedImage.alt}
								className="max-w-full max-h-[85vh] w-auto h-auto object-contain rounded-lg shadow-2xl"
							/>

							{/* Close button — larger touch target on mobile */}
							<button
								onClick={closeLightbox}
								aria-label="Close"
								className="absolute top-3 right-3 sm:top-4 sm:right-4 text-white hover:text-gray-300 transition-colors p-2 bg-black bg-opacity-40 rounded-full">
								<svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
								</svg>
							</button>

							{/* Prev / Next — large touch targets */}
							{images.length > 1 && (
								<>
									<button
										onClick={prevImage}
										aria-label="Previous image"
										className="absolute left-1 sm:left-3 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors p-3 bg-black bg-opacity-40 rounded-full">
										<svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
										</svg>
									</button>
									<button
										onClick={nextImage}
										aria-label="Next image"
										className="absolute right-1 sm:right-3 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors p-3 bg-black bg-opacity-40 rounded-full">
										<svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
										</svg>
									</button>
								</>
							)}

							{/* Image counter */}
							<div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black bg-opacity-50 px-3 py-1 rounded-full">
								{images.findIndex((img) => img.id === selectedImage.id) + 1} / {images.length}
							</div>
						</div>
					</motion.div>
				)}
			</div>
		</div>
	);
};

export default EventGallery;
